import type { HTMLElementEvent } from "@simplybusiness/mobius";
import { Icon, Radio, RadioGroup, TextOrHTML } from "@simplybusiness/mobius";
import { useState } from "react";
import {
  CoverageDiagnosis,
  PricingNudgeIntervention,
} from "@simplybusiness/mobius-interventions";
import { iconMap, isValidIconKey } from "../iconMap";
import { DEFAULT_ADDRESS_SYNC_CONFIG } from "../../../../../../../../constants/addressSyncDefaults";
import { useAddressSyncConfig } from "../../../../../../../../features/questionnaire";
import { useEnvironmentValues } from "../../../../../../../../hooks/environment";
import { useIsClient } from "@simplybusiness/mobius-hooks";
import type { QuestionComponentProps } from "../../../types";
import { getOrientation } from "../orientation";

type PopupType = "indicative_price" | "coverage_diagnosis" | null;

export const RadioButtonsQuestion = ({
  options,
  questionFieldProps,
  setAddressMode,
}: QuestionComponentProps) => {
  const {
    value,
    isDisabled = false,
    questionLabelProps,
    helpInfo,
    ...otherProps
  } = questionFieldProps;
  const [errorMessage, _] = useState<string | undefined>(undefined);
  const [activePopup, setActivePopup] = useState<PopupType>(null);
  const isClient = useIsClient();
  const { site } = useEnvironmentValues();
  const addressSyncConfig = useAddressSyncConfig();

  if (!options?.length) {
    console.warn(`No required options found`);
    return null;
  }

  const handleChange = (event: HTMLElementEvent<HTMLInputElement>) => {
    const { value, name } = event.target;

    const {
      linkQuestionName,
      linkQuestionValue = "yes",
      linkedQuestionName,
    } = addressSyncConfig ?? DEFAULT_ADDRESS_SYNC_CONFIG;

    if (name === linkQuestionName) {
      setAddressMode?.(
        linkedQuestionName,
        value === linkQuestionValue ? "none" : "lookup",
      );
    }

    // Handle WAYHTDT overlay logic
    if (name === "What are you here to do v2") {
      if (value === "idea_of_price") {
        setActivePopup("indicative_price");
      } else if (value === "find_what_insurance_needed") {
        setActivePopup("coverage_diagnosis");
      } else {
        // Clear popups
        setActivePopup(null);
      }
    }

    questionFieldProps.onChange?.(event);
  };

  // Set radio group orientation
  const orientation = getOrientation({ site, optionsCount: options.length });

  return (
    <>
      <RadioGroup
        {...otherProps}
        isDisabled={!isClient || isDisabled}
        value={value as string}
        onChange={handleChange}
        errorMessage={errorMessage || questionFieldProps.errorMessage}
        className="mobius-radio-buttons-question"
        orientation={orientation as "horizontal" | "vertical"}
        autoStack={true}
      >
        {options.map(({ image, label, value }) => {
          if (!image || !isValidIconKey(image)) {
            if (image && !isValidIconKey(image)) {
              console.warn(
                `Invalid icon name provided to the radio button labelled "${label}"`,
              );
            }
            return (
              <Radio
                key={value}
                label={<TextOrHTML text={label} />}
                value={value}
              />
            );
          }

          return (
            <Radio key={value} value={value}>
              <Icon
                icon={iconMap[image]}
                size="sm"
                title={`Business location type: ${label}`}
              />
              <TextOrHTML text={label} />
            </Radio>
          );
        })}
      </RadioGroup>

      {/* IP Widget Overlay */}
      {activePopup === "indicative_price" && (
        <PricingNudgeIntervention
          site={site}
          fireEvent={() => {}}
          onDismiss={() => setActivePopup(null)}
        />
      )}

      {/* Coverage Diagnosis Overlay */}
      {activePopup === "coverage_diagnosis" && (
        <CoverageDiagnosis
          initialShow={true}
          vertical="business"
          site={site}
          fireEvent={(_eventName: string) => {
            // Handle coverage diagnosis events
          }}
        />
      )}
    </>
  );
};
